/**
 * Schedule work after at least `delayMs` (minimum delay).
 * Prefer this over requestIdleCallback when the delay must not fire early
 * (first paint / consent / footer / telemetry). ric's `timeout` option is a
 * *maximum* wait.
 */
export function scheduleDeferredClientTask(
    task: () => void,
    delayMs: number
): () => void {
    if (typeof window === 'undefined') {
        return () => undefined;
    }

    const timeoutId = window.setTimeout(task, delayMs);
    return () => window.clearTimeout(timeoutId);
}

/**
 * Run `task` on the next macrotask after the browser reports that the page's
 * main content has painted (buffered entries included), or after
 * `fallbackMs` if that never arrives / the observer is unsupported. Keeps
 * deferrable work (telemetry SDK download+eval) from competing with the
 * content the visitor is waiting for, without a long fixed delay.
 *
 * Returns a cancel function; the task runs at most once.
 */
export function scheduleAfterMainContentPaint(
    task: () => void,
    options: { fallbackMs: number },
): () => void {
    if (typeof window === 'undefined') {
        return () => undefined;
    }

    let done = false;
    let fallbackId: ReturnType<typeof setTimeout> | undefined;
    let macrotaskId: ReturnType<typeof setTimeout> | undefined;
    let observer: PerformanceObserver | undefined;

    const cleanup = () => {
        if (fallbackId !== undefined) {
            clearTimeout(fallbackId);
            fallbackId = undefined;
        }
        if (macrotaskId !== undefined) {
            clearTimeout(macrotaskId);
            macrotaskId = undefined;
        }
        observer?.disconnect();
        observer = undefined;
    };

    const cancel = () => {
        done = true;
        cleanup();
    };

    const run = () => {
        if (done) return;
        done = true;
        cleanup();
        task();
    };

    const scheduleRun = () => {
        if (done || macrotaskId !== undefined) return;
        observer?.disconnect();
        observer = undefined;
        if (fallbackId !== undefined) {
            clearTimeout(fallbackId);
            fallbackId = undefined;
        }
        macrotaskId = setTimeout(run, 0);
    };

    try {
        if (typeof PerformanceObserver !== 'undefined') {
            observer = new PerformanceObserver((list) => {
                if (list.getEntries().length > 0) {
                    scheduleRun();
                }
            });
            observer.observe({
                type: 'largest-contentful-paint',
                buffered: true,
            } as PerformanceObserverInit);
        }
    } catch {
        /* unsupported — fallback only */
    }

    fallbackId = setTimeout(scheduleRun, options.fallbackMs);

    return cancel;
}
